import React, { useEffect, useState, useRef } from "react";
import { useFetchFeature, FeatureID, CONSENTS, CONSENT_ID, FEATURES } from "../../utils/features";
import { MatomoConsentExpressed } from "./trackingService";
import { sharedEventEmitter } from "../../utils/sharedEvents";
import { getCookieConsent } from "../../../consent/cookieUtils";
/**
 * Root consent orchestrator rendered once at the app level.
 * Each child handles a specific tracking feature: it checks whether the feature is
 * enabled via remote config, then reads the user's cookie consent and forwards the
 * // consent no longer needs to be sent to GTM
    // consent management in GTM has been reworked to read directly from the cookie instead of relying on the dataLayer event,
    // so we can skip pushing consent state updates to GTM entirely.
 */
export default function GTMConsentManager() {
    return (
        <>
            <FeatureConsentHandler
                feature={FEATURES.MATOMO}
                consent={CONSENTS.MATOMO}
                onConsentExpressed={() => MatomoConsentExpressed()}
            ></FeatureConsentHandler>
        </>
    );
}

/**
 * Gate component that checks whether a tracking feature is enabled before handling consent.
 * While the feature flag is loading, renders nothing to avoid premature GTM pushes.
 * If the feature is disabled, immediately pushes `false` so GTM knows consent is denied.
 * @param feature - The feature flag ID used to look up enabled/disabled state.
 * @param consent - The consent category name forwarded to GTM when the feature is enabled.
 * @param onConsentGranted - Optional callback function to be called when consent is granted, used for additional side effects like Matomo's explicit consent signal.
 * @param onConsentExpressed - Optional callback function to be called when consent is expressed (either granted or denied), can be used for side effects that should happen regardless of the consent value.
 * @param onConsentDenied - Optional callback function to be called when consent is denied, can be used for side effects that should only happen on denial.
 */
const FeatureConsentHandler = ({
    feature,
    consent,
    onConsentGranted,
    onConsentExpressed,
    onConsentDenied
}: {
    feature: FeatureID;
    consent: CONSENT_ID;
    onConsentGranted?: () => void;
    onConsentExpressed?: () => void;
    onConsentDenied?: () => void;
}) => {
    const { enabled, loading } = useFetchFeature(feature);
    if (loading === true) return null;
    if (enabled) {
        return (
            <ConsentHandler
                key={feature}
                name={consent}
                onConsentGranted={onConsentGranted}
                onConsentExpressed={onConsentExpressed}
                onConsentDenied={onConsentDenied}
            />
        );
    } else {
        return null;
    }
};

/**
 * Handles the consent state for a given feature and pushes it to GTM.
 * @param name - The name of the consent.
 * @param onConsentGranted - Callback function to be called when consent is granted.
 * @param onConsentExpressed - Callback function to be called when consent is expressed (either granted or denied).
 * @param onConsentDenied - Callback function to be called when consent is denied.
 */
const ConsentHandler = ({
    name,
    onConsentGranted,
    onConsentExpressed,
    onConsentDenied
}: {
    name: CONSENT_ID;
    onConsentGranted?: () => void;
    onConsentExpressed?: () => void;
    onConsentDenied?: () => void;
}) => {
    const consentGiven = useConsentGiven(name);
    const onConsentExpressedRef = useRef(onConsentExpressed);
    const onConsentGrantedRef = useRef(onConsentGranted);
    const onConsentDeniedRef = useRef(onConsentDenied);
    useEffect(() => {
        onConsentExpressedRef.current = onConsentExpressed;
        onConsentGrantedRef.current = onConsentGranted;
        onConsentDeniedRef.current = onConsentDenied;
    });

    useEffect(() => {
        if (consentGiven !== undefined) {
            onConsentExpressedRef.current?.();
            if (consentGiven) {
                onConsentGrantedRef.current?.();
            } else {
                onConsentDeniedRef.current?.();
            }
        }
    }, [name, consentGiven]);
    return null;
};

/**
 * Hook that returns the current consent state for the given category and keeps it
 * in sync with live user changes (e.g. when the consent modal is re-submitted).
 * Returns `undefined` while the cookie has not been set yet (first visit, no decision made).
 * @param name - The consent category to observe.
 */
const useConsentGiven = (name: CONSENT_ID): boolean | undefined => {
    // Seed state from the existing cookie so the value is available on first render
    const [accepted, setAccepted] = useState<boolean | undefined>(getCookieConsent(name));

    useEffect(() => {
        // Re-sync when the user updates their consent preferences at runtime
        function handleConsentChange(consents: Record<string, boolean | undefined>) {
            if (consents[name] !== accepted) {
                setAccepted(consents[name]);
            }
        }
        sharedEventEmitter.on("consent-changed", handleConsentChange);
        return () => {
            sharedEventEmitter.off("consent-changed", handleConsentChange);
        };
    }, [accepted, name]);

    if (!name) {
        console.error("Please provide a name for the consent");
        return false;
    }

    return accepted;
};
